/**
 * Inventory (07) i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys are
 * namespaced under `inventory.*` and `nav.inventory.*`. Layout mirrors automatically under dir="rtl"
 * via CSS logical properties; codes / quantities stay LTR with .nv-ltr-island.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.stock": "Stock / Magasin",
  "nav.inventory.catalogue": "Catalogue réactifs",
  "nav.inventory.reorder": "Réapprovisionnement",
  "nav.inventory.recettes": "Recettes",
  "nav.inventory.charges": "Chargement automate",
  "nav.inventory.recall": "Rappel de lot",
  "nav.inventory.cycles": "Inventaire tournant",

  // shared
  "inventory.title": "Stock / Magasin",
  "inventory.action.refresh": "Actualiser",
  "inventory.action.cancel": "Annuler",
  "inventory.field.required": "Champ requis.",
  "inventory.field.qtyInvalid": "Quantité invalide.",
  "inventory.field.dateInvalid": "Date invalide.",
  "inventory.unit.test": "Test",
  "inventory.unit.ml": "mL",
  "inventory.unit.kit": "Kit",
  "inventory.reactif.pick": "Réactif",
  "inventory.reactif.pick.placeholder": "Choisir un réactif…",

  // catalogue
  "inventory.catalogue.title": "Catalogue des réactifs",
  "inventory.catalogue.subtitle": "Référentiel réactifs, réception de lots, péremption et FEFO.",
  "inventory.catalogue.search": "Rechercher (code, libellé, discipline)…",
  "inventory.catalogue.new": "Nouveau réactif",
  "inventory.catalogue.empty.title": "Aucun réactif",
  "inventory.catalogue.empty.desc": "Créez votre premier réactif pour démarrer le suivi du stock.",
  "inventory.catalogue.empty.search": "Aucun réactif ne correspond à la recherche.",
  "inventory.catalogue.col.code": "Code",
  "inventory.catalogue.col.libelle": "Libellé",
  "inventory.catalogue.col.discipline": "Discipline",
  "inventory.catalogue.col.unite": "Unité",
  "inventory.catalogue.col.seuil": "Seuil d'alerte",
  "inventory.catalogue.col.statut": "Statut",
  "inventory.catalogue.col.actions": "Actions",
  "inventory.catalogue.statut.actif": "Actif",
  "inventory.catalogue.statut.inactif": "Inactif",
  "inventory.catalogue.action.edit": "Modifier",
  "inventory.catalogue.action.receive": "Recevoir un lot",
  "inventory.catalogue.action.lots": "Lots / Péremption",
  "inventory.catalogue.action.recall": "Rappel",

  // reactif form (create / edit)
  "inventory.reactif.create.title": "Nouveau réactif",
  "inventory.reactif.edit.title": "Modifier le réactif",
  "inventory.reactif.code": "Code",
  "inventory.reactif.code.hint": "Identifiant unique du réactif (ex. GLU-500).",
  "inventory.reactif.libelle": "Libellé",
  "inventory.reactif.discipline": "Discipline",
  "inventory.reactif.discipline.hint": "Code discipline (ex. BIOCH, HEMA).",
  "inventory.reactif.automate": "Automate",
  "inventory.reactif.automate.hint": "Optionnel — identifiant de l'automate associé.",
  "inventory.reactif.unite": "Unité de mesure",
  "inventory.reactif.testsParLot": "Tests par lot",
  "inventory.reactif.testsParLot.hint": "Optionnel — capacité d'un lot, en tests.",
  "inventory.reactif.seuil": "Seuil d'alerte",
  "inventory.reactif.seuil.hint": "En-deçà, le réactif passe en BAS puis RUPTURE.",
  "inventory.reactif.statut": "Statut",
  "inventory.reactif.create.submit": "Créer le réactif",
  "inventory.reactif.edit.submit": "Enregistrer",
  "inventory.reactif.create.success": "Réactif créé.",
  "inventory.reactif.edit.success": "Réactif mis à jour.",

  // lot reception
  "inventory.lot.receive.title": "Recevoir un lot",
  "inventory.lot.numero": "Numéro de lot",
  "inventory.lot.numero.hint": "Référence du fabricant figurant sur l'emballage.",
  "inventory.lot.peremption": "Date de péremption",
  "inventory.lot.quantite": "Quantité reçue",
  "inventory.lot.quantite.hint": "Quantité initiale dans l'unité du réactif.",
  "inventory.lot.prixAchat": "Prix d'achat",
  "inventory.lot.prixAchat.hint": "Optionnel — coût unitaire (centimes), pour la valorisation.",
  "inventory.lot.receive.submit": "Réceptionner",
  "inventory.lot.receive.success": "Lot réceptionné. Entrée en stock enregistrée.",

  // lots / risk drawer
  "inventory.lots.title": "Lots et péremption",
  "inventory.lots.subtitle": "Vue FEFO — premier périmé, premier sorti.",
  "inventory.lots.empty.title": "Aucun lot",
  "inventory.lots.empty.desc": "Réceptionnez un lot pour ce réactif.",
  "inventory.lots.col.numero": "N° lot",
  "inventory.lots.col.days": "Jours restants",
  "inventory.lots.col.tier": "Risque",
  "inventory.lots.col.actions": "Actions",
  "inventory.lots.tier.critique": "Critique",
  "inventory.lots.tier.alerte": "Alerte",
  "inventory.lots.tier.ok": "OK",
  "inventory.lots.days.expired": "Périmé",
  "inventory.lots.action.quarantine": "Mettre en quarantaine",
  "inventory.lots.action.release": "Libérer",
  "inventory.lots.action.recall": "Rappel de lot",
  "inventory.lots.action.movements": "Mouvements",
  // NOVA-058 — per-lot movement history (append-only ledger)
  "inventory.movements.title": "Historique des mouvements",
  "inventory.movements.subtitle": "Journal append-only du lot — chaque mouvement et le solde cumulé.",
  "inventory.movements.empty.title": "Aucun mouvement",
  "inventory.movements.empty.desc": "Ce lot n'a encore aucun mouvement enregistré.",
  "inventory.movements.col.at": "Horodatage",
  "inventory.movements.col.type": "Type",
  "inventory.movements.col.quantite": "Quantité",
  "inventory.movements.col.solde": "Solde",
  "inventory.movements.col.motif": "Motif",
  "inventory.movements.type.entree": "Entrée",
  "inventory.movements.type.sortie": "Sortie",
  "inventory.movements.type.ajustement": "Ajustement",
  "inventory.movements.type.peremption": "Péremption",
  "inventory.lots.statut.motif": "Motif",
  "inventory.lots.statut.motif.hint": "Justification (médico-légal, ≥ 2 caractères).",
  "inventory.lots.quarantine.title": "Mettre le lot en quarantaine",
  "inventory.lots.release.title": "Libérer le lot",
  "inventory.lots.quarantine.submit": "Mettre en quarantaine",
  "inventory.lots.release.submit": "Libérer le lot",
  "inventory.lots.quarantine.success": "Lot mis en quarantaine.",
  "inventory.lots.release.success": "Lot libéré.",

  // sweep
  "inventory.sweep.cta": "Balayer les périmés",
  "inventory.sweep.confirm.title": "Balayage des lots périmés",
  "inventory.sweep.confirm.warn":
    "Cette opération fait passer en « périmé » tous les lots dont la date est dépassée. Action idempotente et auditée.",
  "inventory.sweep.submit": "Lancer le balayage",
  "inventory.sweep.success.none": "Aucun lot à balayer.",
  "inventory.sweep.success.some": "{n} lot(s) marqué(s) périmé(s).",

  // reorder dashboard
  "inventory.reorder.title": "Tableau de réapprovisionnement",
  "inventory.reorder.subtitle": "État du stock par réactif : OK, bas ou en rupture.",
  "inventory.reorder.bucket.OK": "OK",
  "inventory.reorder.bucket.BAS": "Bas",
  "inventory.reorder.bucket.RUPTURE": "Rupture",
  "inventory.reorder.kpi.total": "Réactifs suivis",
  "inventory.reorder.kpi.OK": "En stock",
  "inventory.reorder.kpi.BAS": "Stock bas",
  "inventory.reorder.kpi.RUPTURE": "En rupture",
  "inventory.reorder.chart.title": "Répartition du stock",
  "inventory.reorder.chart.empty": "Aucune donnée de stock à afficher.",
  "inventory.reorder.col.code": "Code",
  "inventory.reorder.col.libelle": "Libellé",
  "inventory.reorder.col.onHand": "En main",
  "inventory.reorder.col.seuil": "Seuil",
  "inventory.reorder.col.bucket": "État",
  "inventory.reorder.empty.title": "Catalogue vide",
  "inventory.reorder.empty.desc": "Ajoutez des réactifs au catalogue pour suivre le réapprovisionnement.",
  "inventory.reorder.loading": "Calcul des seuils…",

  // recette editor
  "inventory.recettes.title": "Recettes de consommation",
  "inventory.recettes.subtitle":
    "Quantité de réactif consommée par test et par contrôle, par analyse et automate.",
  "inventory.recettes.new": "Nouvelle recette",
  "inventory.recettes.analyse": "Code analyse",
  "inventory.recettes.analyse.hint": "Code de l'analyse consommatrice (ex. GLU).",
  "inventory.recettes.automate": "Automate",
  "inventory.recettes.automate.hint": "Optionnel — vide = joker (toutes plateformes).",
  "inventory.recettes.methode": "Méthode",
  "inventory.recettes.methode.hint": "Optionnel — vide = joker (toutes méthodes).",
  "inventory.recettes.qParTest": "Quantité par test",
  "inventory.recettes.qParTest.hint": "Consommation pour un test patient.",
  "inventory.recettes.qParControle": "Quantité par contrôle",
  "inventory.recettes.qParControle.hint": "Consommation pour un point de CQ (0 si aucun).",
  "inventory.recettes.submit": "Créer la recette",
  "inventory.recettes.success": "Recette enregistrée.",
  "inventory.recettes.help.title": "Comment ça marche",
  "inventory.recettes.help.body":
    "À chaque résultat, la recette la plus spécifique (automate + méthode) l'emporte sur le joker. Une recette par réactif est retenue par exécution.",

  // charge loader
  "inventory.charges.title": "Chargement automate",
  "inventory.charges.subtitle": "Désignez le lot actif d'un réactif sur un automate (un seul lot actif).",
  "inventory.charges.automate": "Automate",
  "inventory.charges.automate.hint": "Identifiant de l'automate qui reçoit le lot.",
  "inventory.charges.lot": "Lot à charger",
  "inventory.charges.lot.hint": "Lot actif du réactif à rendre opérationnel.",
  "inventory.charges.lot.placeholder": "Choisir un lot…",
  "inventory.charges.lot.none": "Aucun lot actif pour ce réactif. Réceptionnez d'abord un lot.",
  "inventory.charges.discipline": "Discipline",
  "inventory.charges.discipline.hint": "Optionnel — code discipline.",
  "inventory.charges.submit": "Charger le lot",
  "inventory.charges.success": "Lot chargé. Le lot précédent a été déchargé le cas échéant.",
  "inventory.charges.dechargedPrior": "Lot précédent déchargé.",

  // recall worklist
  "inventory.recall.title": "Rappel de lot → résultats",
  "inventory.recall.subtitle":
    "Prévisualisez les résultats impactés par un lot, puis confirmez la mise en quarantaine.",
  "inventory.recall.lot": "Lot à rappeler",
  "inventory.recall.lot.placeholder": "Choisir un lot…",
  "inventory.recall.preview.cta": "Prévisualiser l'impact",
  "inventory.recall.preview.loading": "Recherche des résultats impactés…",
  "inventory.recall.preview.title": "Résultats impactés",
  "inventory.recall.preview.count": "{n} résultat(s) validé(s)/corrigé(s) portent ce lot.",
  "inventory.recall.preview.none": "Aucun résultat validé ou corrigé n'est rattaché à ce lot.",
  "inventory.recall.preview.statut": "Statut du lot",
  "inventory.recall.confirm.cta": "Confirmer le rappel",
  "inventory.recall.confirm.title": "Confirmer le rappel du lot",
  "inventory.recall.confirm.warn":
    "Le rappel met le lot en quarantaine et alerte le biologiste pour révision. Les valeurs des résultats ne sont jamais modifiées automatiquement.",
  "inventory.recall.motif": "Motif du rappel",
  "inventory.recall.motif.hint": "Justification (médico-légal, ≥ 3 caractères).",
  "inventory.recall.confirm.submit": "Confirmer le rappel",
  "inventory.recall.confirm.success": "Rappel confirmé. {n} résultat(s) à réviser.",
  "inventory.recall.confirm.noop": "Le lot était déjà en quarantaine — aucune action.",
  "inventory.recall.resultats.title": "Identifiants des résultats",

  // lot lifecycle statut labels (recall picker — NOVA-027)
  "inventory.lots.statut.actif": "Actif",
  "inventory.lots.statut.quarantaine": "Quarantaine",
  "inventory.lots.statut.perime": "Périmé",
  "inventory.lots.statut.epuise": "Épuisé",

  // cycle-count (INV-8 — NOVA-024)
  "inventory.cycles.title": "Inventaire tournant",
  "inventory.cycles.subtitle":
    "Comptage physique du stock : ouvrir, compter, réviser, poster. Le post écrit un ajustement signé par lot variant.",
  "inventory.cycles.open.title": "Ouvrir un inventaire",
  "inventory.cycles.libelle": "Libellé",
  "inventory.cycles.libelle.placeholder": "Inventaire mensuel — Biochimie",
  "inventory.cycles.scope": "Périmètre",
  "inventory.cycles.scope.global": "Global (tout le stock)",
  "inventory.cycles.scope.discipline": "Par discipline",
  "inventory.cycles.scope.reactif": "Par réactif",
  "inventory.cycles.discipline": "Discipline",
  "inventory.cycles.discipline.hint": "Code discipline (ex. BIOCH, HEMA).",
  "inventory.cycles.open.cta": "Ouvrir l'inventaire",
  "inventory.cycles.open.success": "Inventaire ouvert. {n} lot(s) à compter.",
  "inventory.cycles.session.title": "Session de comptage",
  "inventory.cycles.empty.title": "Aucun lot dans le périmètre",
  "inventory.cycles.empty.desc": "Aucun lot actif ne correspond au périmètre choisi.",
  "inventory.cycles.col.numero": "N° lot",
  "inventory.cycles.col.systeme": "Quantité système",
  "inventory.cycles.col.comptee": "Quantité comptée",
  "inventory.cycles.col.ecart": "Écart",
  "inventory.cycles.count.cta": "Compter",
  "inventory.cycles.review.cta": "Passer en révision",
  "inventory.cycles.review.success": "Inventaire en révision.",
  "inventory.cycles.post.cta": "Poster l'inventaire",
  "inventory.cycles.post.success": "Inventaire posté. {n} ajustement(s) enregistré(s).",
  "inventory.cycles.post.noop": "Inventaire déjà posté — aucune action.",
  "inventory.cycles.cancel.cta": "Annuler l'inventaire",
  "inventory.cycles.cancel.success": "Inventaire annulé.",
  "inventory.cycles.statut.brouillon": "Brouillon",
  "inventory.cycles.statut.en_cours": "En cours",
  "inventory.cycles.statut.en_revue": "En révision",
  "inventory.cycles.statut.postee": "Postée",
  "inventory.cycles.statut.annulee": "Annulée",
  "inventory.cycles.summary.title": "Résumé du post",
  "inventory.cycles.summary.count": "{n} ajustement(s) écrit(s) au grand livre.",
  "inventory.cycles.summary.none": "Aucun écart — aucun ajustement n'a été nécessaire.",
  "inventory.cycles.summary.lot": "Lot",
  "inventory.cycles.summary.mouvement": "Mouvement",
};

const AR: Record<string, string> = {
  // nav
  "nav.section.stock": "المخزون",
  "nav.inventory.catalogue": "كتالوج الكواشف",
  "nav.inventory.reorder": "إعادة التموين",
  "nav.inventory.recettes": "الوصفات",
  "nav.inventory.charges": "تحميل الجهاز",
  "nav.inventory.recall": "سحب دفعة",
  "nav.inventory.cycles": "الجرد الدوري",

  // shared
  "inventory.title": "المخزون",
  "inventory.action.refresh": "تحديث",
  "inventory.action.cancel": "إلغاء",
  "inventory.field.required": "حقل مطلوب.",
  "inventory.field.qtyInvalid": "كمية غير صالحة.",
  "inventory.field.dateInvalid": "تاريخ غير صالح.",
  "inventory.unit.test": "اختبار",
  "inventory.unit.ml": "مل",
  "inventory.unit.kit": "علبة",
  "inventory.reactif.pick": "الكاشف",
  "inventory.reactif.pick.placeholder": "اختر كاشفا…",

  // catalogue
  "inventory.catalogue.title": "كتالوج الكواشف",
  "inventory.catalogue.subtitle": "مرجع الكواشف، استلام الدفعات، الصلاحية و FEFO.",
  "inventory.catalogue.search": "بحث (الرمز، التسمية، التخصص)…",
  "inventory.catalogue.new": "كاشف جديد",
  "inventory.catalogue.empty.title": "لا توجد كواشف",
  "inventory.catalogue.empty.desc": "أنشئ أول كاشف لبدء متابعة المخزون.",
  "inventory.catalogue.empty.search": "لا يوجد كاشف مطابق للبحث.",
  "inventory.catalogue.col.code": "الرمز",
  "inventory.catalogue.col.libelle": "التسمية",
  "inventory.catalogue.col.discipline": "التخصص",
  "inventory.catalogue.col.unite": "الوحدة",
  "inventory.catalogue.col.seuil": "عتبة التنبيه",
  "inventory.catalogue.col.statut": "الحالة",
  "inventory.catalogue.col.actions": "إجراءات",
  "inventory.catalogue.statut.actif": "نشط",
  "inventory.catalogue.statut.inactif": "غير نشط",
  "inventory.catalogue.action.edit": "تعديل",
  "inventory.catalogue.action.receive": "استلام دفعة",
  "inventory.catalogue.action.lots": "الدفعات / الصلاحية",
  "inventory.catalogue.action.recall": "سحب",

  // reactif form
  "inventory.reactif.create.title": "كاشف جديد",
  "inventory.reactif.edit.title": "تعديل الكاشف",
  "inventory.reactif.code": "الرمز",
  "inventory.reactif.code.hint": "معرّف فريد للكاشف (مثال GLU-500).",
  "inventory.reactif.libelle": "التسمية",
  "inventory.reactif.discipline": "التخصص",
  "inventory.reactif.discipline.hint": "رمز التخصص (مثال BIOCH، HEMA).",
  "inventory.reactif.automate": "الجهاز",
  "inventory.reactif.automate.hint": "اختياري — معرّف الجهاز المرتبط.",
  "inventory.reactif.unite": "وحدة القياس",
  "inventory.reactif.testsParLot": "اختبارات لكل دفعة",
  "inventory.reactif.testsParLot.hint": "اختياري — سعة الدفعة بالاختبارات.",
  "inventory.reactif.seuil": "عتبة التنبيه",
  "inventory.reactif.seuil.hint": "دونها يصبح الكاشف منخفضا ثم نافدا.",
  "inventory.reactif.statut": "الحالة",
  "inventory.reactif.create.submit": "إنشاء الكاشف",
  "inventory.reactif.edit.submit": "حفظ",
  "inventory.reactif.create.success": "تم إنشاء الكاشف.",
  "inventory.reactif.edit.success": "تم تحديث الكاشف.",

  // lot reception
  "inventory.lot.receive.title": "استلام دفعة",
  "inventory.lot.numero": "رقم الدفعة",
  "inventory.lot.numero.hint": "مرجع المصنّع المدوّن على العبوة.",
  "inventory.lot.peremption": "تاريخ الصلاحية",
  "inventory.lot.quantite": "الكمية المستلمة",
  "inventory.lot.quantite.hint": "الكمية الأولية بوحدة الكاشف.",
  "inventory.lot.prixAchat": "سعر الشراء",
  "inventory.lot.prixAchat.hint": "اختياري — التكلفة (سنتيم) للتقييم.",
  "inventory.lot.receive.submit": "استلام",
  "inventory.lot.receive.success": "تم استلام الدفعة. سُجّل الدخول إلى المخزون.",

  // lots / risk drawer
  "inventory.lots.title": "الدفعات والصلاحية",
  "inventory.lots.subtitle": "عرض FEFO — الأقرب انتهاءً يخرج أولا.",
  "inventory.lots.empty.title": "لا توجد دفعات",
  "inventory.lots.empty.desc": "استلم دفعة لهذا الكاشف.",
  "inventory.lots.col.numero": "رقم الدفعة",
  "inventory.lots.col.days": "الأيام المتبقية",
  "inventory.lots.col.tier": "الخطر",
  "inventory.lots.col.actions": "إجراءات",
  "inventory.lots.tier.critique": "حرج",
  "inventory.lots.tier.alerte": "تنبيه",
  "inventory.lots.tier.ok": "سليم",
  "inventory.lots.days.expired": "منتهي",
  "inventory.lots.action.quarantine": "حجر صحي",
  "inventory.lots.action.release": "تحرير",
  "inventory.lots.action.recall": "سحب الدفعة",
  "inventory.lots.action.movements": "الحركات",
  // NOVA-058 — per-lot movement history
  "inventory.movements.title": "سجل الحركات",
  "inventory.movements.subtitle": "سجل الدفعة (إضافة فقط) — كل حركة والرصيد التراكمي.",
  "inventory.movements.empty.title": "لا توجد حركات",
  "inventory.movements.empty.desc": "لا توجد أي حركة مسجّلة لهذه الدفعة بعد.",
  "inventory.movements.col.at": "الطابع الزمني",
  "inventory.movements.col.type": "النوع",
  "inventory.movements.col.quantite": "الكمية",
  "inventory.movements.col.solde": "الرصيد",
  "inventory.movements.col.motif": "السبب",
  "inventory.movements.type.entree": "إدخال",
  "inventory.movements.type.sortie": "إخراج",
  "inventory.movements.type.ajustement": "تعديل",
  "inventory.movements.type.peremption": "انتهاء صلاحية",
  "inventory.lots.statut.motif": "السبب",
  "inventory.lots.statut.motif.hint": "تبرير (طبي-قانوني، ≥ حرفان).",
  "inventory.lots.quarantine.title": "حجر الدفعة",
  "inventory.lots.release.title": "تحرير الدفعة",
  "inventory.lots.quarantine.submit": "حجر صحي",
  "inventory.lots.release.submit": "تحرير الدفعة",
  "inventory.lots.quarantine.success": "تم حجر الدفعة.",
  "inventory.lots.release.success": "تم تحرير الدفعة.",

  // sweep
  "inventory.sweep.cta": "كنس المنتهية",
  "inventory.sweep.confirm.title": "كنس الدفعات المنتهية",
  "inventory.sweep.confirm.warn":
    "تنقل هذه العملية كل الدفعات منتهية الصلاحية إلى « منتهية ». عملية متكافئة ومدققة.",
  "inventory.sweep.submit": "بدء الكنس",
  "inventory.sweep.success.none": "لا توجد دفعة للكنس.",
  "inventory.sweep.success.some": "تم وسم {n} دفعة كمنتهية.",

  // reorder dashboard
  "inventory.reorder.title": "لوحة إعادة التموين",
  "inventory.reorder.subtitle": "حالة المخزون لكل كاشف: سليم، منخفض أو نافد.",
  "inventory.reorder.bucket.OK": "سليم",
  "inventory.reorder.bucket.BAS": "منخفض",
  "inventory.reorder.bucket.RUPTURE": "نافد",
  "inventory.reorder.kpi.total": "الكواشف المتابَعة",
  "inventory.reorder.kpi.OK": "متوفر",
  "inventory.reorder.kpi.BAS": "مخزون منخفض",
  "inventory.reorder.kpi.RUPTURE": "نافد",
  "inventory.reorder.chart.title": "توزيع المخزون",
  "inventory.reorder.chart.empty": "لا توجد بيانات مخزون للعرض.",
  "inventory.reorder.col.code": "الرمز",
  "inventory.reorder.col.libelle": "التسمية",
  "inventory.reorder.col.onHand": "المتوفر",
  "inventory.reorder.col.seuil": "العتبة",
  "inventory.reorder.col.bucket": "الحالة",
  "inventory.reorder.empty.title": "كتالوج فارغ",
  "inventory.reorder.empty.desc": "أضف كواشف إلى الكتالوج لمتابعة إعادة التموين.",
  "inventory.reorder.loading": "حساب العتبات…",

  // recette editor
  "inventory.recettes.title": "وصفات الاستهلاك",
  "inventory.recettes.subtitle": "كمية الكاشف المستهلكة لكل اختبار ولكل ضبط، حسب التحليل والجهاز.",
  "inventory.recettes.new": "وصفة جديدة",
  "inventory.recettes.analyse": "رمز التحليل",
  "inventory.recettes.analyse.hint": "رمز التحليل المستهلك (مثال GLU).",
  "inventory.recettes.automate": "الجهاز",
  "inventory.recettes.automate.hint": "اختياري — فارغ = شامل (كل المنصات).",
  "inventory.recettes.methode": "الطريقة",
  "inventory.recettes.methode.hint": "اختياري — فارغ = شامل (كل الطرق).",
  "inventory.recettes.qParTest": "الكمية لكل اختبار",
  "inventory.recettes.qParTest.hint": "الاستهلاك لاختبار مريض.",
  "inventory.recettes.qParControle": "الكمية لكل ضبط",
  "inventory.recettes.qParControle.hint": "الاستهلاك لنقطة ضبط الجودة (0 إن لم توجد).",
  "inventory.recettes.submit": "إنشاء الوصفة",
  "inventory.recettes.success": "تم حفظ الوصفة.",
  "inventory.recettes.help.title": "كيف تعمل",
  "inventory.recettes.help.body":
    "عند كل نتيجة، تتفوق الوصفة الأكثر تحديدا (جهاز + طريقة) على الشاملة. تُعتمد وصفة واحدة لكل كاشف في كل تشغيل.",

  // charge loader
  "inventory.charges.title": "تحميل الجهاز",
  "inventory.charges.subtitle": "عيّن الدفعة النشطة لكاشف على جهاز (دفعة نشطة واحدة فقط).",
  "inventory.charges.automate": "الجهاز",
  "inventory.charges.automate.hint": "معرّف الجهاز المستقبِل للدفعة.",
  "inventory.charges.lot": "الدفعة المراد تحميلها",
  "inventory.charges.lot.hint": "الدفعة النشطة للكاشف لجعلها قيد التشغيل.",
  "inventory.charges.lot.placeholder": "اختر دفعة…",
  "inventory.charges.lot.none": "لا توجد دفعة نشطة لهذا الكاشف. استلم دفعة أولا.",
  "inventory.charges.discipline": "التخصص",
  "inventory.charges.discipline.hint": "اختياري — رمز التخصص.",
  "inventory.charges.submit": "تحميل الدفعة",
  "inventory.charges.success": "تم تحميل الدفعة. وأُفرغت الدفعة السابقة إن وُجدت.",
  "inventory.charges.dechargedPrior": "أُفرغت الدفعة السابقة.",

  // recall worklist
  "inventory.recall.title": "سحب دفعة ← النتائج",
  "inventory.recall.subtitle": "عاين النتائج المتأثرة بدفعة، ثم أكّد الحجر الصحي.",
  "inventory.recall.lot": "الدفعة المراد سحبها",
  "inventory.recall.lot.placeholder": "اختر دفعة…",
  "inventory.recall.preview.cta": "معاينة الأثر",
  "inventory.recall.preview.loading": "البحث عن النتائج المتأثرة…",
  "inventory.recall.preview.title": "النتائج المتأثرة",
  "inventory.recall.preview.count": "{n} نتيجة مصادق عليها/مصححة تحمل هذه الدفعة.",
  "inventory.recall.preview.none": "لا توجد نتيجة مصادق عليها أو مصححة مرتبطة بهذه الدفعة.",
  "inventory.recall.preview.statut": "حالة الدفعة",
  "inventory.recall.confirm.cta": "تأكيد السحب",
  "inventory.recall.confirm.title": "تأكيد سحب الدفعة",
  "inventory.recall.confirm.warn":
    "يضع السحب الدفعة في الحجر الصحي وينبّه البيولوجي للمراجعة. لا تُعدّل قيم النتائج تلقائيا أبدا.",
  "inventory.recall.motif": "سبب السحب",
  "inventory.recall.motif.hint": "تبرير (طبي-قانوني، ≥ 3 أحرف).",
  "inventory.recall.confirm.submit": "تأكيد السحب",
  "inventory.recall.confirm.success": "تم تأكيد السحب. {n} نتيجة للمراجعة.",
  "inventory.recall.confirm.noop": "كانت الدفعة محجوزة مسبقا — لا إجراء.",
  "inventory.recall.resultats.title": "معرّفات النتائج",

  // lot lifecycle statut labels (recall picker — NOVA-027)
  "inventory.lots.statut.actif": "نشطة",
  "inventory.lots.statut.quarantaine": "محجوزة",
  "inventory.lots.statut.perime": "منتهية",
  "inventory.lots.statut.epuise": "نافدة",

  // cycle-count (INV-8 — NOVA-024)
  "inventory.cycles.title": "الجرد الدوري",
  "inventory.cycles.subtitle":
    "العدّ الفيزيائي للمخزون: فتح، عدّ، مراجعة، ترحيل. يكتب الترحيل تسوية موقّعة لكل دفعة متغيرة.",
  "inventory.cycles.open.title": "فتح جرد",
  "inventory.cycles.libelle": "التسمية",
  "inventory.cycles.libelle.placeholder": "جرد شهري — الكيمياء الحيوية",
  "inventory.cycles.scope": "النطاق",
  "inventory.cycles.scope.global": "شامل (كل المخزون)",
  "inventory.cycles.scope.discipline": "حسب التخصص",
  "inventory.cycles.scope.reactif": "حسب الكاشف",
  "inventory.cycles.discipline": "التخصص",
  "inventory.cycles.discipline.hint": "رمز التخصص (مثال BIOCH، HEMA).",
  "inventory.cycles.open.cta": "فتح الجرد",
  "inventory.cycles.open.success": "تم فتح الجرد. {n} دفعة للعدّ.",
  "inventory.cycles.session.title": "جلسة العدّ",
  "inventory.cycles.empty.title": "لا توجد دفعات في النطاق",
  "inventory.cycles.empty.desc": "لا توجد دفعة نشطة تطابق النطاق المختار.",
  "inventory.cycles.col.numero": "رقم الدفعة",
  "inventory.cycles.col.systeme": "كمية النظام",
  "inventory.cycles.col.comptee": "الكمية المعدودة",
  "inventory.cycles.col.ecart": "الفرق",
  "inventory.cycles.count.cta": "عدّ",
  "inventory.cycles.review.cta": "إلى المراجعة",
  "inventory.cycles.review.success": "الجرد قيد المراجعة.",
  "inventory.cycles.post.cta": "ترحيل الجرد",
  "inventory.cycles.post.success": "تم ترحيل الجرد. {n} تسوية مسجّلة.",
  "inventory.cycles.post.noop": "الجرد مُرحّل مسبقا — لا إجراء.",
  "inventory.cycles.cancel.cta": "إلغاء الجرد",
  "inventory.cycles.cancel.success": "تم إلغاء الجرد.",
  "inventory.cycles.statut.brouillon": "مسودة",
  "inventory.cycles.statut.en_cours": "جارٍ",
  "inventory.cycles.statut.en_revue": "قيد المراجعة",
  "inventory.cycles.statut.postee": "مُرحّلة",
  "inventory.cycles.statut.annulee": "ملغاة",
  "inventory.cycles.summary.title": "ملخص الترحيل",
  "inventory.cycles.summary.count": "{n} تسوية مكتوبة في دفتر الأستاذ.",
  "inventory.cycles.summary.none": "لا فرق — لم تكن أي تسوية ضرورية.",
  "inventory.cycles.summary.lot": "الدفعة",
  "inventory.cycles.summary.mouvement": "الحركة",
};

let registered = false;

/** Idempotent — registers the inventory namespace into both dictionaries exactly once. */
export function registerInventoryMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
